/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React from 'react';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    Badge,
    ChannelAiringInfo,
    ContentDescription,
    ContentTitle,
    Credits,
    Duration,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
    StarRating,
    StatusMessage,
    TimeAiringInfo,
} from '../ui-kit';
import { VodPreviewFormatter } from '../VodPreview/VodPreviewFormatter';
import { VodScreenType } from '../VodPreview/VodPreviewProps';
import type { TInfoCardContentUtil } from './InfoCardProps';

/**
 * MovieInfoCard
 * Ref.: http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/core_ux/components/components.html#movie_info_card
 * There is no line limit or truncation enforced for metadata display. Display all metadata
 * available for each part, for example, title, description, or actors list.
 *
 * See redline for layout.
 *
 * Detail mode: Side caption
 *      - Part 0
 *      - Part 1
 *      - Part 2
 *      - Part 3
 *      - Part 4
 *      - Part 5
 *      - Detail mode: Action
 */
export const getMovieInfoCardContent: TInfoCardContentUtil = (metadata) => {
    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const url: string = assetService.getMoviePosterImageUrl(metadata.contentId || '');

    const pcRatingNames = PreviewComponent.getParentalRatingNames(
        metadata.internalRatingList || [],
    );
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        metadata.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );

    const vodPreviewFormatter = new VodPreviewFormatter(metadata, VodScreenType.VOD_INFO_CARD);
    const entitlementAndAvailabilityStatus = vodPreviewFormatter.getCombinedStatusMessage();

    const getHeaderContent = () => {
        return (
            <React.Fragment>
                <ShowcaseBanner
                    bannerUrl={url || metadata.episodePreviewImageUrl || ''}
                    fallbackText={metadata.title || ''}
                />
                <div className="metadata">
                    <div className="side-captions">
                        <Badge
                            text={metadata.videoResolution || metadata.channel?.videoResolution}
                            uppercase={true}
                        />
                        <Badge text={metadata.isNew ? 'New' : ''} />
                        <ParentalControlRating ratingNames={pcRatingNames} />
                        <Separator />
                        <Genres categoryLabel={metadata.categoryLabel} />
                        <Separator />
                        <Duration duration={metadata.duration} />
                    </div>
                    <Credits creditString={metadata.creditString} />
                    <StarRating starRating={metadata.starRating} />
                </div>
            </React.Fragment>
        );
    };

    const getBodyContent = (): JSX.Element => {
        return (
            <React.Fragment>
                {!entitlementAndAvailabilityStatus && (
                    <>
                        {metadata.channel && (
                            <ChannelAiringInfo
                                channelNumber={metadata.channel.channelNumber}
                                callSign={metadata.channel.callSign}
                                videoResolution={
                                    metadata.channel.videoResolution || metadata.videoResolution
                                }
                                isThreeD={metadata.isThreeD}
                            />
                        )}
                        {metadata.channel && metadata.startTime && metadata.endTime && (
                            <Separator />
                        )}
                        {metadata.startTime && metadata.endTime && (
                            <TimeAiringInfo
                                startTime={metadata.startTime}
                                endTime={metadata.endTime}
                            />
                        )}
                    </>
                )}
                <StatusMessage
                    entitlementAndAvailabilityStatus={entitlementAndAvailabilityStatus}
                    status={metadata.recordingStatus}
                    isKeepForeverRecording={metadata.isKeepForeverRecording}
                    showType={metadata.previewType}
                    isPartialRecording={metadata.isPartialRecording}
                    startTime={metadata.startTime}
                    endTime={metadata.endTime}
                    duration={metadata.recordingDuration}
                    recordingAvailability={metadata.recordingAvailability}
                />
                <ContentTitle
                    title={metadata.title}
                    showType={metadata.previewType}
                    year={metadata.movieYear}
                />
                <ContentDescription description={sContentDescription} />
            </React.Fragment>
        );
    };

    return {
        headerContent: getHeaderContent(),
        bodyContent: getBodyContent(),
    };
};
